import React, { useCallback, useEffect, useMemo, useState } from 'react';
import GlobalLeaderboardDataGrid, { GlobalLeaderboardCompany } from './GlobalLeaderboardDataGrid';
import { INDEX_METRICS } from './indexMetrics';
import { INDUSTRY_OPTIONS, REGION_COUNTRIES, labelForIndustry } from './globalLeaderboardOptions';
import { STOCK_EXCHANGES, stockExchangeLabel } from './stockExchangeOptions';

type FilterState = {
  query: string;
  exchanges: string[];
  industries: string[];
  countries: string[];
  indexes: string[];
  sortField: string;
  sortDirection: 'asc' | 'desc';
};

type LeaderboardMeta = {
  count?: number;
  filters?: {
    exchanges?: string[];
    industry_groups?: string[];
    countries?: string[];
    indexes?: string[];
  };
};

type Props = {
  data?: GlobalLeaderboardCompany[];
  meta?: LeaderboardMeta;
  initialError?: string;
};

const STORAGE_KEY = 'di.leaderboard.global.v1';
const FILTER_RAIL_STORAGE_KEY = 'di.leaderboard.filters.collapsed.v1';
const DEFAULT_STATE: FilterState = {
  query: '',
  exchanges: [],
  industries: [],
  countries: [],
  indexes: [],
  sortField: 'valueScore',
  sortDirection: 'desc',
};

const SORTABLE_FIELDS = new Set([
  'listing', 'industry', 'valueScore', 'consideration', 'decision', 'price', 'change', 'paybackTime',
]);

const normalizeSortField = (field: string) => field === 'country' ? 'listing' : field;

const unique = (values: string[]) => Array.from(new Set(values.filter(Boolean)));
const normalizeExchanges = (values: string[]) => unique(values.map(value => value.trim().toUpperCase()));

function initialState(): FilterState {
  if (typeof window === 'undefined') return DEFAULT_STATE;

  const params = new URLSearchParams(window.location.search);
  const stateKeys = ['q', 'exchange', 'industry', 'region', 'country', 'index', 'sort', 'direction'];
  const hasUrlState = stateKeys.some(key => params.has(key));

  if (hasUrlState) {
    const sortField = normalizeSortField(params.get('sort') || DEFAULT_STATE.sortField);
    const direction = params.get('direction');
    return {
      query: params.get('q') || '',
      exchanges: normalizeExchanges(params.getAll('exchange')),
      industries: unique(params.getAll('industry')),
      countries: unique([
        ...params.getAll('country'),
        ...params.getAll('region').reduce(
          (allCountries, region) => allCountries.concat(REGION_COUNTRIES[region] || []),
          [] as string[]
        ),
      ]),
      indexes: unique(params.getAll('index')),
      sortField: SORTABLE_FIELDS.has(sortField) ? sortField : DEFAULT_STATE.sortField,
      sortDirection: direction === 'asc' ? 'asc' : 'desc',
    };
  }

  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
    const savedSortField = normalizeSortField(saved.sortField || '');
    const sortField = SORTABLE_FIELDS.has(savedSortField) ? savedSortField : DEFAULT_STATE.sortField;
    return {
      query: typeof saved.query === 'string' ? saved.query : '',
      exchanges: normalizeExchanges(Array.isArray(saved.exchanges) ? saved.exchanges : []),
      industries: unique(Array.isArray(saved.industries) ? saved.industries : []),
      countries: unique(Array.isArray(saved.countries) ? saved.countries : []),
      indexes: unique(Array.isArray(saved.indexes) ? saved.indexes : []),
      sortField,
      sortDirection: saved.sortDirection === 'asc' ? 'asc' : 'desc',
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function initialFilterRailCollapsed(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return window.localStorage.getItem(FILTER_RAIL_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

const sameValues = (left: string[], right: string[]) =>
  [...left].sort().join('|') === [...right].sort().join('|');

const summarizeGeographies = (countries: string[]) => {
  const regions = Object.entries(REGION_COUNTRIES)
    .filter(([, regionCountries]) => regionCountries.every(country => countries.includes(country)))
    .map(([region]) => region);
  const countriesInSelectedRegions = new Set(
    regions.reduce(
      (allCountries, region) => allCountries.concat(REGION_COUNTRIES[region]),
      [] as string[]
    )
  );
  return {
    regions,
    individualCountries: countries.filter(country => !countriesInSelectedRegions.has(country)),
  };
};

function GlobalLeaderboard({ data = [], meta = {}, initialError = '' }: Props) {
  const [state, setState] = useState<FilterState>(initialState);
  const [companies, setCompanies] = useState<GlobalLeaderboardCompany[]>(data);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(initialError);
  const [retryCount, setRetryCount] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [filterRailCollapsed, setFilterRailCollapsed] = useState(initialFilterRailCollapsed);

  const geographySummary = summarizeGeographies(state.countries);
  const selectedFilterCount =
    state.exchanges.length +
    state.industries.length +
    geographySummary.regions.length +
    geographySummary.individualCountries.length +
    state.indexes.length;
  const filterKey = JSON.stringify({
    exchanges: [...state.exchanges].sort(),
    industry_groups: [...state.industries].sort(),
    countries: [...state.countries].sort(),
    indexes: [...state.indexes].sort(),
  });

  const initialFiltersMatch =
    sameValues(state.exchanges, meta.filters?.exchanges || []) &&
    sameValues(state.industries, meta.filters?.industry_groups || []) &&
    sameValues(state.countries, meta.filters?.countries || []) &&
    sameValues(state.indexes, meta.filters?.indexes || []);

  useEffect(() => {
    const toggleButton = document.querySelector<HTMLButtonElement>(
      '.app-navigation-toggle[data-toggle="leaderboard-filters"]'
    );
    if (!toggleButton) return;

    const toggleFilterRail = () => setFilterRailCollapsed(collapsed => !collapsed);
    toggleButton.addEventListener('click', toggleFilterRail);
    return () => toggleButton.removeEventListener('click', toggleFilterRail);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(FILTER_RAIL_STORAGE_KEY, String(filterRailCollapsed));
    } catch {
      // The filter rail still works when storage is blocked or unavailable.
    }

    const toggleButton = document.querySelector<HTMLButtonElement>(
      '.app-navigation-toggle[data-toggle="leaderboard-filters"]'
    );
    const filterCount = toggleButton?.querySelector<HTMLElement>('.leaderboard-filter-count');
    if (!toggleButton) return;

    const activeFilterLabel = selectedFilterCount === 1 ? 'filter' : 'filters';
    toggleButton.setAttribute('aria-expanded', String(!filterRailCollapsed));
    toggleButton.setAttribute(
      'aria-label',
      filterRailCollapsed
        ? `Show refine results${selectedFilterCount ? ` (${selectedFilterCount} active ${activeFilterLabel})` : ''}`
        : 'Hide refine results'
    );

    if (filterCount) {
      filterCount.textContent = String(selectedFilterCount);
      filterCount.classList.toggle('is-visible', filterRailCollapsed && selectedFilterCount > 0);
    }
  }, [filterRailCollapsed, selectedFilterCount]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Browsing can continue when storage is blocked or unavailable.
    }

    const params = new URLSearchParams();
    if (state.query.trim()) params.set('q', state.query);
    state.exchanges.forEach(value => params.append('exchange', value));
    state.industries.forEach(value => params.append('industry', value));
    const summarizedGeographies = summarizeGeographies(state.countries);
    summarizedGeographies.regions.forEach(value => params.append('region', value));
    summarizedGeographies.individualCountries.forEach(value => params.append('country', value));
    state.indexes.forEach(value => params.append('index', value));
    if (state.sortField !== DEFAULT_STATE.sortField) params.set('sort', state.sortField);
    if (state.sortDirection !== DEFAULT_STATE.sortDirection) params.set('direction', state.sortDirection);
    const query = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
  }, [state]);

  useEffect(() => {
    if (initialFiltersMatch && retryCount === 0) {
      setCompanies(data);
      setError(initialError);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const params = new URLSearchParams();
    state.exchanges.forEach(value => params.append('exchanges[]', value));
    state.industries.forEach(value => params.append('industry_groups[]', value));
    state.countries.forEach(value => params.append('countries[]', value));
    state.indexes.forEach(value => params.append('indexes[]', value));

    setLoading(true);
    setError('');
    fetch(`/leaderboard/data?${params.toString()}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then(async response => {
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || 'The leaderboard could not be loaded.');
        return payload;
      })
      .then(payload => setCompanies(payload.data || []))
      .catch(requestError => {
        if (requestError.name !== 'AbortError') {
          setCompanies([]);
          setError(requestError.message || 'The leaderboard could not be loaded.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [filterKey, retryCount]);

  useEffect(() => {
    if (!mobileFiltersOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    const drawer = document.getElementById('mobile-leaderboard-filters');
    const focusableSelector = 'button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
    const focusableElements = () => Array.from(drawer?.querySelectorAll<HTMLElement>(focusableSelector) || []);
    document.body.style.overflow = 'hidden';
    focusableElements()[0]?.focus();

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileFiltersOpen(false);
      if (event.key !== 'Tab') return;

      const elements = focusableElements();
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [mobileFiltersOpen]);

  const visibleCompanies = useMemo(() => {
    const query = state.query.trim().toLowerCase();
    if (!query) return companies;

    return companies.filter(company => [
      company.ticker,
      company.company,
      company.country,
      company.stock_exchange_symbol,
      company.adr ? 'ADR' : '',
      company.industry,
      company.value_score,
      company.default_valuation?.recommendation,
      company.default_valuation?.decision,
      company.default_valuation?.price_in_words,
      company.default_valuation?.margin_of_safety_in_words,
      company.default_valuation?.payback_time?.current_payback_time_number,
      company.quote?.change_percent,
    ].some(value => `${value ?? ''}`.toLowerCase().includes(query)));
  }, [companies, state.query]);

  const updateList = (key: 'exchanges' | 'industries' | 'countries' | 'indexes', value: string, checked: boolean) => {
    setState(current => ({
      ...current,
      [key]: checked ? unique([...current[key], value]) : current[key].filter(item => item !== value),
    }));
  };

  const toggleRegion = (region: string, checked: boolean) => {
    const regionCountries = REGION_COUNTRIES[region] || [];
    setState(current => ({
      ...current,
      countries: checked
        ? unique([...current.countries, ...regionCountries])
        : current.countries.filter(country => !regionCountries.includes(country)),
    }));
  };

  const clearAll = () => {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore blocked storage */ }
    setState(DEFAULT_STATE);
    setError('');
  };

  const sortModel = useMemo(
    () => [{ field: state.sortField, sort: state.sortDirection }],
    [state.sortField, state.sortDirection]
  );
  const handleSortModelChange = useCallback((model: any[]) => {
    const nextSort = model[0];
    if (!nextSort || !SORTABLE_FIELDS.has(nextSort.field)) return;
    const nextDirection = nextSort.sort === 'asc' ? 'asc' : 'desc';
    setState(current => {
      if (current.sortField === nextSort.field && current.sortDirection === nextDirection) {
        return current;
      }

      return {
        ...current,
        sortField: nextSort.field,
        sortDirection: nextDirection,
      };
    });
  }, []);

  const renderFilterPanel = (idPrefix: string) => (
    <FilterPanel
      idPrefix={idPrefix}
      state={state}
      selectedCount={selectedFilterCount}
      updateList={updateList}
      toggleRegion={toggleRegion}
      clearAll={clearAll}
      closeMobile={() => setMobileFiltersOpen(false)}
    />
  );

  return (
    <div className={`global-leaderboard-page${filterRailCollapsed ? ' glb-filters-collapsed' : ''}`}>
      <style>{pageStyles}</style>

      <aside
        id="leaderboard-filter-panel"
        className="glb-sidebar"
        aria-label="Leaderboard filters"
        aria-hidden={filterRailCollapsed}
      >
        {renderFilterPanel('desktop')}
      </aside>

      <main className="glb-main">
        <header className="glb-hero">
          <div className="glb-eyebrow">Global company discovery</div>
          <h1>Discover wonderful businesses around the world</h1>
          <p className="glb-intro">
           We rank public companies worldwide based on tried and true value investing principles. We analyze each company's 10+ years of growth in profitability, revenue, cash flow, debt management, and ROIC to give it a score. Explore the highest-ranked companies worldwide below, then filter by exchange, market index, geography, or industry to narrow your search. Check out our guide on <a href="https://decode-investing.medium.com/how-to-identify-a-wonderful-business-6f7afdc6a5c6">How to Identify a Wonderful Business</a>.
          </p>
        </header>

        <section className="glb-table-card" aria-labelledby="leaderboard-results-heading">
            <div className="glb-toolbar">
              <div>
                <h2 id="leaderboard-results-heading">Leaderboard</h2>
                <div className="glb-result-count" aria-live="polite">
                  <strong>{visibleCompanies.length.toLocaleString()}</strong>
                  {state.query.trim() ? ` of ${companies.length.toLocaleString()}` : ''} companies
                </div>
              </div>
              <button
                type="button"
                className="glb-mobile-filter-button"
                onClick={() => setMobileFiltersOpen(true)}
                aria-expanded={mobileFiltersOpen}
                aria-controls="mobile-leaderboard-filters"
              >
                Filters {selectedFilterCount > 0 && <span>{selectedFilterCount}</span>}
              </button>
            </div>

            <div className="glb-search-row">
              <label className="glb-search" htmlFor="global-company-search">
                <svg aria-hidden="true" viewBox="0 0 20 20"><path fill="currentColor" fillRule="evenodd" d="M12.9 14.32a8 8 0 111.414-1.414l4.387 4.387a1 1 0 01-1.414 1.414L12.9 14.32zM14 8a6 6 0 11-12 0 6 6 0 0112 0z" clipRule="evenodd" /></svg>
                <input
                  id="global-company-search"
                  type="search"
                  value={state.query}
                  onChange={event => setState(current => ({ ...current, query: event.target.value }))}
                  placeholder="Search ticker, company, exchange, country, or industry…"
                />
                {state.query && <button type="button" onClick={() => setState(current => ({ ...current, query: '' }))} aria-label="Clear keyword search">Clear</button>}
              </label>
            </div>

            <ActiveFilters state={state} updateList={updateList} toggleRegion={toggleRegion} clearAll={clearAll} />

            <div className="glb-legend" aria-label="Valuation color legend">
              <span><i className="glb-dot glb-dot-green" />Worth considering</span>
              <span><i className="glb-dot glb-dot-neutral" />Review carefully</span>
              <span><i className="glb-dot glb-dot-red" />Avoid at this price</span>
            </div>

            {error && (
              <div className="glb-state glb-error" role="alert">
                <div><strong>We couldn’t update these results.</strong><span>{error}</span></div>
                <button type="button" onClick={() => setRetryCount(count => count + 1)}>Try again</button>
              </div>
            )}

            {!error && (
              !loading && visibleCompanies.length === 0 ? (
                <div className="glb-state glb-empty">
                  <strong>No companies match these filters</strong>
                  <span>Try removing a filter or using a broader keyword.</span>
                  <button type="button" onClick={clearAll}>Clear all filters</button>
                </div>
              ) : (
                <GlobalLeaderboardDataGrid
                  data={visibleCompanies}
                  loading={loading}
                  sortModel={sortModel}
                  onSortModelChange={handleSortModelChange}
                />
              )
            )}
        </section>

        <p className="glb-disclaimer">
          Rankings are a research starting point, not investment advice. Prices may be delayed and values can vary by reporting currency. Always verify the company’s filings and do your own research.
        </p>
      </main>

      {mobileFiltersOpen && (
        <div className="glb-mobile-overlay" role="presentation" onMouseDown={event => {
          if (event.target === event.currentTarget) setMobileFiltersOpen(false);
        }}>
          <aside id="mobile-leaderboard-filters" className="glb-mobile-drawer" role="dialog" aria-modal="true" aria-label="Filter leaderboard">
            {renderFilterPanel('mobile')}
          </aside>
        </div>
      )}
    </div>
  );
}

type FilterPanelProps = {
  idPrefix: string;
  state: FilterState;
  selectedCount: number;
  updateList: (key: 'exchanges' | 'industries' | 'countries' | 'indexes', value: string, checked: boolean) => void;
  toggleRegion: (region: string, checked: boolean) => void;
  clearAll: () => void;
  closeMobile: () => void;
};

function FilterPanel({ idPrefix, state, selectedCount, updateList, toggleRegion, clearAll, closeMobile }: FilterPanelProps) {
  const [exchangeSearch, setExchangeSearch] = useState('');
  const [industrySearch, setIndustrySearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [indexSearch, setIndexSearch] = useState('');

  const exchangeQuery = exchangeSearch.trim().toLowerCase();
  const exchanges = STOCK_EXCHANGES.filter(exchange =>
    !exchangeQuery ||
    exchange.exchange.toLowerCase().includes(exchangeQuery) ||
    exchange.name.toLowerCase().includes(exchangeQuery) ||
    exchange.country.toLowerCase().includes(exchangeQuery)
  );
  const industries = INDUSTRY_OPTIONS.filter(option => option.label.toLowerCase().includes(industrySearch.toLowerCase()));
  const geographySummary = summarizeGeographies(state.countries);
  const indexGroups = INDEX_METRICS.reduce((groups, index) => {
    if (!index.name.toLowerCase().includes(indexSearch.toLowerCase()) &&
        !index.geography.toLowerCase().includes(indexSearch.toLowerCase()) &&
        !index.marketSegment.toLowerCase().includes(indexSearch.toLowerCase())) return groups;
    groups[index.geography] = [...(groups[index.geography] || []), index];
    return groups;
  }, {} as Record<string, typeof INDEX_METRICS>);

  return (
    <div className="glb-filter-panel">
      <div className="glb-filter-heading">
        <div><strong>Refine results</strong><span>{selectedCount ? `${selectedCount} selected` : 'Optional filters'}</span></div>
        <button type="button" className="glb-drawer-close" onClick={closeMobile} aria-label="Close filters">×</button>
      </div>

      <details className="glb-filter-section">
        <summary>Exchange <span>{state.exchanges.length || ''}</span></summary>
        <FilterSearch id={`${idPrefix}-exchange-filter-search`} value={exchangeSearch} onChange={setExchangeSearch} placeholder="Find an exchange or country" />
        <div className="glb-option-list">
          {exchanges.map(exchange => (
            <CheckOption
              key={exchange.exchange}
              label={exchange.exchange}
              detail={exchange.country}
              checked={state.exchanges.includes(exchange.exchange)}
              onChange={checked => updateList('exchanges', exchange.exchange, checked)}
            />
          ))}
        </div>
      </details>

      <details className="glb-filter-section">
        <summary>Index <span>{state.indexes.length || ''}</span></summary>
        <FilterSearch id={`${idPrefix}-index-filter-search`} value={indexSearch} onChange={setIndexSearch} placeholder="Find an index or market" />
        <div className="glb-option-list">
          {Object.entries(indexGroups).map(([geography, indexes]) => (
            <div className="glb-index-group" key={geography}>
              <div className="glb-group-label">{geography}</div>
              {indexes.map(index => (
                <CheckOption
                  key={index.key}
                  label={index.name}
                  detail={index.marketSegment}
                  checked={state.indexes.includes(index.key)}
                  onChange={checked => updateList('indexes', index.key, checked)}
                />
              ))}
            </div>
          ))}
        </div>
      </details>

      <details className="glb-filter-section">
        <summary>Region &amp; Country <span>{geographySummary.regions.length + geographySummary.individualCountries.length || ''}</span></summary>
        <FilterSearch id={`${idPrefix}-country-filter-search`} value={countrySearch} onChange={setCountrySearch} placeholder="Find a country" />
        <div className="glb-option-list glb-country-list">
          {Object.entries(REGION_COUNTRIES).map(([region, countries]) => {
            const matchingCountries = countries.filter(country => country.toLowerCase().includes(countrySearch.toLowerCase()));
            if (countrySearch && matchingCountries.length === 0 && !region.toLowerCase().includes(countrySearch.toLowerCase())) return null;
            const selectedInRegion = countries.filter(country => state.countries.includes(country)).length;
            const regionChecked = selectedInRegion === countries.length;
            return (
              <div className="glb-region" key={region}>
                <CheckOption
                  label={region}
                  detail={selectedInRegion > 0 && !regionChecked ? `${selectedInRegion}/${countries.length}` : `${countries.length}`}
                  checked={regionChecked}
                  onChange={checked => toggleRegion(region, checked)}
                  emphasized
                />
                <div className="glb-region-countries">
                  {matchingCountries.map(country => (
                    <CheckOption key={country} label={country} checked={state.countries.includes(country)} onChange={checked => updateList('countries', country, checked)} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </details>

      <details className="glb-filter-section">
        <summary>Industry <span>{state.industries.length || ''}</span></summary>
        <FilterSearch id={`${idPrefix}-industry-filter-search`} value={industrySearch} onChange={setIndustrySearch} placeholder="Find an industry" />
        <div className="glb-option-list">
          {industries.map(option => (
            <CheckOption key={option.value} label={option.label} checked={state.industries.includes(option.value)} onChange={checked => updateList('industries', option.value, checked)} />
          ))}
        </div>
      </details>

      <div className="glb-filter-actions">
        <button type="button" className="glb-clear-all" onClick={clearAll} disabled={!selectedCount && !state.query}>Clear all</button>
        <button type="button" className="glb-filter-done" onClick={closeMobile}>Show results</button>
      </div>
    </div>
  );
}

function FilterSearch({ id, value, onChange, placeholder }: { id: string; value: string; onChange: (value: string) => void; placeholder: string }) {
  return (
    <label className="glb-filter-search" htmlFor={id}>
      <span className="sr-only">{placeholder}</span>
      <input id={id} type="search" value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} />
    </label>
  );
}

function CheckOption({ label, detail, checked, onChange, emphasized = false }: { label: string; detail?: string | false; checked: boolean; onChange: (checked: boolean) => void; emphasized?: boolean }) {
  return (
    <label className={`glb-check-option${emphasized ? ' glb-check-emphasized' : ''}`}>
      <input type="checkbox" checked={checked} onChange={event => onChange(event.target.checked)} />
      <span>{label}</span>
      {detail && <small>{detail}</small>}
    </label>
  );
}

function ActiveFilters({ state, updateList, toggleRegion, clearAll }: { state: FilterState; updateList: FilterPanelProps['updateList']; toggleRegion: FilterPanelProps['toggleRegion']; clearAll: () => void }) {
  const geographySummary = summarizeGeographies(state.countries);
  const chips = [
    ...state.exchanges.map(value => ({ key: 'exchanges' as const, value, label: stockExchangeLabel(value) })),
    ...state.indexes.map(value => ({ key: 'indexes' as const, value, label: INDEX_METRICS.find(index => index.key === value)?.name || value })),
    ...geographySummary.regions.map(value => ({ key: 'regions' as const, value, label: value })),
    ...geographySummary.individualCountries.map(value => ({ key: 'countries' as const, value, label: value })),
    ...state.industries.map(value => ({ key: 'industries' as const, value, label: labelForIndustry(value) })),
  ];
  if (!chips.length) return null;

  return (
    <div className="glb-active-filters" aria-label="Active filters">
      {chips.map(chip => (
        <button
          key={`${chip.key}-${chip.value}`}
          type="button"
          onClick={() => chip.key === 'regions' ? toggleRegion(chip.value, false) : updateList(chip.key, chip.value, false)}
          aria-label={`Remove ${chip.label} filter`}
        >
          {chip.label}<span aria-hidden="true">×</span>
        </button>
      ))}
      <button type="button" className="glb-active-clear" onClick={clearAll}>Clear all</button>
    </div>
  );
}

const pageStyles = `
  .global-leaderboard-page { display: grid; grid-template-columns: 285px minmax(0, 1fr); height: calc(100vh - 70px); height: calc(100dvh - 70px); min-height: 0; overflow: hidden; color: #172033; background: #f5f7fb; transition: grid-template-columns .2s ease; }
  .global-leaderboard-page.glb-filters-collapsed { grid-template-columns: 0 minmax(0, 1fr); }
  .glb-sidebar { min-width: 0; height: 100%; min-height: 0; overflow-x: hidden; overflow-y: auto; overscroll-behavior: contain; scrollbar-gutter: stable; scrollbar-width: thin; -webkit-overflow-scrolling: touch; opacity: 1; visibility: visible; background: #fff; border-right: 1px solid #dfe5ef; transition: opacity .14s ease, border-color .14s ease; }
  .glb-filters-collapsed .glb-sidebar { opacity: 0; visibility: hidden; pointer-events: none; border-right-color: transparent; }
  .glb-main { min-width: 0; height: 100%; min-height: 0; overflow-x: hidden; overflow-y: auto; overscroll-behavior: contain; scrollbar-gutter: stable; scrollbar-width: thin; -webkit-overflow-scrolling: touch; padding: 1rem 1.35rem 3rem; }
  .global-leaderboard-shell .app-navigation-toggle { position: relative; }
  .leaderboard-filter-count { display: none; position: absolute; top: 13px; right: 10px; place-items: center; min-width: 17px; height: 17px; padding: 0 4px; border: 2px solid #fff; border-radius: 999px; background: #155eef; color: #fff; font-size: 10px; font-weight: 800; line-height: 1; }
  .leaderboard-filter-count.is-visible { display: inline-grid; }
  .glb-hero { padding: 0 0 1.15rem; max-width: 1120px; }
  .glb-eyebrow { color: #175cd3; font-size: .72rem; font-weight: 800; letter-spacing: .11em; text-transform: uppercase; margin-bottom: .42rem; }
  .glb-hero h1 { color: #101828; font-size: clamp(2rem, 3.2vw, 3rem); line-height: 1.05; letter-spacing: -.04em; margin: 0 0 .62rem; max-width: 1000px; }
  .glb-intro { color: #475467; font-size: .96rem; line-height: 1.55; max-width: 960px; margin: 0 0 .75rem; }
  .glb-filter-panel { min-height: 100%; background: #fff; }
  .glb-table-card { background: #fff; border: 1px solid #dfe5ef; box-shadow: 0 10px 30px rgba(16, 24, 40, .055); }
  .glb-filter-heading { position: sticky; top: 0; z-index: 2; display: flex; justify-content: space-between; align-items: center; padding: 1rem; border-bottom: 1px solid #e9edf3; background: #fff; }
  .glb-filter-heading > div { display: flex; flex-direction: column; gap: .12rem; }
  .glb-filter-heading strong { color: #101828; font-size: .98rem; }
  .glb-filter-heading span { color: #667085; font-size: .75rem; }
  .glb-drawer-close { display: none; border: 0; background: transparent; color: #475467; font-size: 1.7rem; line-height: 1; cursor: pointer; }
  .glb-filter-section { border-bottom: 1px solid #e9edf3; }
  .glb-filter-section summary { display: flex; align-items: center; cursor: pointer; list-style: none; padding: .88rem 1rem .6rem; color: #344054; font-size: .84rem; font-weight: 800; }
  .glb-filter-section summary::-webkit-details-marker { display: none; }
  .glb-filter-section summary::after { content: '⌄'; color: #98a2b3; margin-left: auto; font-size: 1rem; }
  .glb-filter-section summary span { display: inline-grid; place-items: center; min-width: 1.25rem; height: 1.25rem; border-radius: 999px; margin-left: .45rem; background: #eef4ff; color: #175cd3; font-size: .68rem; }
  .glb-filter-search { display: block; padding: 0 .85rem .55rem; }
  .glb-filter-search input { width: 100%; border: 1px solid #d0d5dd; background: #fff; color: #101828; border-radius: 8px; font-size: .78rem; padding: .48rem .62rem; outline: none; }
  .glb-filter-search input:focus { border-color: #528bff; box-shadow: 0 0 0 3px rgba(21, 94, 239, .12); }
  .glb-option-list { padding: 0 .65rem .65rem; }
  .glb-check-option { display: flex; align-items: center; gap: .55rem; min-height: 30px; padding: .25rem .35rem; border-radius: 7px; color: #475467; cursor: pointer; font-size: .78rem; }
  .glb-check-option:hover { background: #f6f8fb; color: #101828; }
  .glb-check-option input { width: 15px; height: 15px; accent-color: #155eef; flex: none; }
  .glb-check-option span { min-width: 0; flex: 1; }
  .glb-check-option small { color: #98a2b3; font-size: .66rem; }
  .glb-check-emphasized { color: #344054; font-weight: 750; margin-top: .2rem; }
  .glb-region-countries { border-left: 1px solid #e4e7ec; margin-left: .75rem; padding-left: .35rem; }
  .glb-group-label { color: #667085; font-size: .68rem; font-weight: 800; letter-spacing: .045em; text-transform: uppercase; padding: .55rem .35rem .2rem; }
  .glb-filter-actions { display: flex; gap: .5rem; padding: .85rem; background: #fff; }
  .glb-filter-actions button { border-radius: 8px; min-height: 36px; font-weight: 750; font-size: .78rem; cursor: pointer; }
  .glb-clear-all { width: 100%; border: 1px solid #d0d5dd; background: #fff; color: #344054; }
  .glb-clear-all:disabled { opacity: .45; cursor: not-allowed; }
  .glb-filter-done { display: none; flex: 1; border: 1px solid #155eef; background: #155eef; color: #fff; }
  .glb-table-card { border-radius: 18px; overflow: hidden; }
  .glb-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 1.1rem 1.25rem .7rem; }
  .glb-toolbar h2 { color: #101828; font-size: 1.08rem; margin: 0 0 .15rem; }
  .glb-result-count { color: #667085; font-size: .8rem; }
  .glb-result-count strong { color: #344054; }
  .glb-mobile-filter-button { display: none; align-items: center; gap: .4rem; border: 1px solid #b2c5e7; background: #f5f8ff; color: #1849a9; padding: .5rem .7rem; border-radius: 9px; font-weight: 750; }
  .glb-mobile-filter-button span { display: inline-grid; place-items: center; min-width: 1.25rem; height: 1.25rem; background: #155eef; color: white; border-radius: 999px; font-size: .68rem; }
  .glb-search-row { padding: .25rem 1.25rem .8rem; }
  .glb-search { display: flex; align-items: center; gap: .65rem; width: min(100%, 720px); min-height: 44px; border: 1px solid #cfd6e2; background: #fbfcfe; border-radius: 11px; padding: 0 .85rem; }
  .glb-search:focus-within { background: #fff; border-color: #528bff; box-shadow: 0 0 0 3px rgba(21, 94, 239, .11); }
  .glb-search svg { width: 17px; color: #667085; flex: none; }
  .glb-search input { flex: 1; min-width: 0; border: 0; outline: 0; background: transparent; color: #101828; font-size: .9rem; }
  .glb-search button { border: 0; background: transparent; color: #175cd3; font-size: .76rem; font-weight: 750; cursor: pointer; }
  .glb-active-filters { display: flex; gap: .4rem; align-items: center; overflow-x: auto; padding: 0 1.25rem .7rem; scrollbar-width: thin; }
  .glb-active-filters button { display: inline-flex; gap: .42rem; align-items: center; white-space: nowrap; border: 1px solid #b2c5e7; background: #eef4ff; color: #1849a9; border-radius: 999px; padding: .28rem .55rem; font-size: .7rem; font-weight: 700; cursor: pointer; }
  .glb-active-filters button span { font-size: .95rem; line-height: .7; }
  .glb-active-filters .glb-active-clear { border-color: transparent; background: transparent; color: #667085; text-decoration: underline; }
  .glb-legend { display: flex; flex-wrap: wrap; gap: .85rem; padding: .6rem 1.25rem; border-top: 1px solid #edf0f5; border-bottom: 1px solid #edf0f5; color: #667085; font-size: .68rem; }
  .glb-legend span { display: inline-flex; align-items: center; gap: .35rem; }
  .glb-dot { width: 7px; height: 7px; border-radius: 50%; background: #98a2b3; }
  .glb-dot-green { background: #12b76a; } .glb-dot-red { background: #f04438; }
  .glb-state { margin: .8rem 1.25rem; padding: 1rem; border-radius: 10px; display: flex; justify-content: space-between; gap: 1rem; align-items: center; }
  .glb-state div, .glb-empty { flex-direction: column; align-items: flex-start; }
  .glb-state strong, .glb-state span { display: block; }
  .glb-state span { font-size: .8rem; margin-top: .2rem; }
  .glb-state button { border: 1px solid #b2c5e7; color: #1849a9; background: #fff; border-radius: 8px; padding: .45rem .7rem; font-weight: 750; cursor: pointer; }
  .glb-grid-placeholder { display: grid; place-items: center; min-height: 320px; color: #667085; font-size: .84rem; background: linear-gradient(110deg, #fff 25%, #f7f9fc 45%, #fff 65%); }
  .glb-error { background: #fff4f2; border: 1px solid #fecdca; color: #b42318; }
  .glb-empty { min-height: 240px; justify-content: center; background: #f8fafc; color: #475467; }
  .glb-disclaimer { color: #667085; font-size: .75rem; line-height: 1.55; margin: .85rem .3rem 0; }
  .glb-mobile-overlay { display: none; }
  .dark-mode .global-leaderboard-page { color: #e4e7ec; background: #0e1117; }
  .dark-mode .leaderboard-filter-count { border-color: #191c24; background: #84adff; color: #101828; }
  .dark-mode .glb-sidebar { background: #111722; border-color: #303744; }
  .dark-mode .glb-hero h1, .dark-mode .glb-toolbar h2, .dark-mode .glb-filter-heading strong { color: #f2f4f7; }
  .dark-mode .glb-intro, .dark-mode .glb-result-count, .dark-mode .glb-disclaimer { color: #98a2b3; }
  .dark-mode .glb-eyebrow { color: #84adff; }
  .dark-mode .glb-filter-panel, .dark-mode .glb-filter-heading, .dark-mode .glb-filter-actions { background: #111722; }
  .dark-mode .glb-table-card { background: #111722; border-color: #303744; box-shadow: 0 12px 32px rgba(0, 0, 0, .22); }
  .dark-mode .glb-filter-heading, .dark-mode .glb-filter-section { border-color: #252c38; }
  .dark-mode .glb-filter-heading span, .dark-mode .glb-filter-section summary, .dark-mode .glb-check-option, .dark-mode .glb-result-count strong { color: #b9c0cc; }
  .dark-mode .glb-filter-section summary span { background: rgba(21, 94, 239, .2); color: #84adff; }
  .dark-mode .glb-filter-search input, .dark-mode .glb-search { background: #161b26; border-color: #3b4350; color: #f2f4f7; }
  .dark-mode .glb-filter-search input, .dark-mode .glb-search input { color: #f2f4f7; }
  .dark-mode .glb-check-option:hover { background: #1b2230; color: #f2f4f7; }
  .dark-mode .glb-region-countries { border-color: #303744; }
  .dark-mode .glb-group-label, .dark-mode .glb-check-option small { color: #858e9d; }
  .dark-mode .glb-clear-all, .dark-mode .glb-state button { background: #161b26; border-color: #475467; color: #d0d5dd; }
  .dark-mode .glb-search:focus-within { background: #161b26; }
  .dark-mode .glb-active-filters button { background: rgba(21, 94, 239, .15); border-color: rgba(132, 173, 255, .3); color: #b2ccff; }
  .dark-mode .glb-legend { border-color: #252c38; color: #98a2b3; }
  .dark-mode .glb-error { background: rgba(180, 35, 24, .15); border-color: rgba(253, 162, 155, .28); color: #fda29b; }
  .dark-mode .glb-empty { background: #161b26; color: #b9c0cc; }
  .dark-mode .glb-grid-placeholder { color: #98a2b3; background: linear-gradient(110deg, #111722 25%, #1b2230 45%, #111722 65%); }
  @media (max-width: 1050px) {
    .global-leaderboard-page { grid-template-columns: 250px minmax(0, 1fr); }
    .glb-main { padding-left: 1rem; padding-right: 1rem; }
  }
  @media (max-width: 991px) {
    .global-leaderboard-page { display: block; height: 100%; overflow: hidden; }
    .glb-sidebar { display: none; }
    .glb-main { height: 100%; padding: .9rem .85rem 2.5rem; overflow-y: auto; }
    .glb-hero { padding-top: 0; }
    .glb-mobile-filter-button { display: inline-flex; }
    .glb-mobile-overlay { position: fixed; display: flex; justify-content: flex-end; inset: 0; z-index: 1200; background: rgba(16, 24, 40, .55); }
    .glb-mobile-drawer { width: min(92vw, 380px); height: 100%; overflow-y: auto; background: #fff; box-shadow: -12px 0 40px rgba(16, 24, 40, .2); }
    .glb-mobile-drawer .glb-filter-panel { min-height: 100%; border: 0; border-radius: 0; box-shadow: none; }
    .glb-mobile-drawer .glb-drawer-close, .glb-mobile-drawer .glb-filter-done { display: block; }
    .glb-mobile-drawer .glb-clear-all { flex: 1; }
    .dark-mode .glb-mobile-drawer { background: #111722; }
  }
  @media (max-width: 540px) {
    .glb-hero h1 { font-size: 2rem; }
    .glb-intro { font-size: .93rem; }
    .glb-table-card { border-radius: 13px; }
    .glb-toolbar, .glb-search-row { padding-left: .85rem; padding-right: .85rem; }
    .glb-active-filters, .glb-legend { padding-left: .85rem; padding-right: .85rem; }
    .glb-legend { gap: .5rem; }
  }
  @media (prefers-reduced-motion: reduce) {
    .global-leaderboard-page, .glb-sidebar { transition: none; }
  }
`;

export default GlobalLeaderboard;
